import DashboardIcon from '@mui/icons-material/Dashboard';
import {
	AppBar,
	Avatar,
	Box,
	CssBaseline,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
} from '@mui/material';
import { LogoSVG } from 'assets/icons';
import { STORAGE_KEYS } from 'constants/api';
import partition from 'lodash/partition';
import { useGetUserProfile } from 'modules/auth';
import * as React from 'react';
import { useCallback } from 'react';
import { FiLogOut } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routers';
import { admin } from 'routers/routes';

const drawerWidth = 280;

interface Props {
	/**
	 * Injected by the documentation to work in an iframe.
	 * You won't need it on your project.
	 */
	window?: () => Window;
	children: React.ReactNode;
}

const NavItem = (props: any) => {
	const { route } = props;
	const navigate = useNavigate();
	const location = useLocation();

	return (
		<React.Fragment>
			<ListItem
				key={route.path}
				disablePadding
				sx={{ color: route.path === location.pathname ? '#1570EF' : '#6C737F' }}
			>
				<ListItemButton selected={route.path === location.pathname} onClick={() => navigate(route.path)}>
					<ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
						{route.icon ? <route.icon /> : <DashboardIcon />}
					</ListItemIcon>
					<ListItemText primary={route.name} />
				</ListItemButton>
			</ListItem>
		</React.Fragment>
	);
};

export default function AdminLayout(props: Props) {
	const { window, children } = props;
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const navigate = useNavigate();
	const { data: profile } = useGetUserProfile();

	const handleSignOut = useCallback(() => {
		localStorage.removeItem(STORAGE_KEYS.AccessToken);
		localStorage.removeItem('role');
		navigate(ROUTES.LOGIN);
	}, []);

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};
	const navRoutes = partition(admin, route => route.position !== 'bottom');

	const drawer = (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Divider />
			<List
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					flexGrow: 1,
				}}
			>
				<Box>
					{navRoutes[0].map((route: any, id: number) => !route.invisible && <NavItem key={id} route={route} />)}
				</Box>
				<Box>
					{navRoutes[1].map((route: any, id: number) => !route.invisible && <NavItem key={id} route={route} />)}
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							padding: 2,
							borderTop: '1px solid #EAECF0',
						}}
					>
						<Box display="flex" alignItems="center">
							<Avatar alt="avatar" src={profile?.image_url || LogoSVG} />
						</Box>
						<IconButton onClick={handleSignOut}>
							<FiLogOut />
						</IconButton>
					</Box>
				</Box>
			</List>
		</Box>
	);

	const container = window !== undefined ? () => window().document.body : undefined;

	return (
		<Box sx={{ display: 'flex' }}>
			<CssBaseline />
			<AppBar
				position="fixed"
				sx={{
					width: { sm: `calc(100% - ${drawerWidth}px)` },
					ml: { sm: `${drawerWidth}px` },
				}}
			></AppBar>
			<Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="mailbox folders">
				{/* The implementation can be swapped with js to avoid SEO duplication of links. */}
				<Drawer
					container={container}
					variant="temporary"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					ModalProps={{
						keepMounted: true, // Better open performance on mobile.
					}}
					sx={{
						display: { xs: 'block', sm: 'none' },
						'& .MuiDrawer-paper': {
							boxSizing: 'border-box',
							width: drawerWidth,
						},
					}}
				>
					{drawer}
				</Drawer>
				<Drawer
					variant="permanent"
					sx={{
						display: { xs: 'none', sm: 'block' },
						'& .MuiDrawer-paper': {
							boxSizing: 'border-box',
							width: drawerWidth,
						},
					}}
					open
				>
					{drawer}
				</Drawer>
			</Box>
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					p: 3,
					width: { sm: `calc(100% - ${drawerWidth}px)` },
				}}
			>
				{children}
			</Box>
		</Box>
	);
}
